import React, { useCallback, useState } from 'react';
import { DndProvider } from 'react-dnd';
import cloneDeep from 'lodash.clonedeep';

import useReorderDragDropBackend from 'hooks/useReorderDragDropBackend';
import useMedia from 'hooks/useMedia';

import LabeledCheckbox from 'components/base/LabeledCheckbox/LabeledCheckbox';
import ViewItem, { Displayname } from 'components/CustomizeView/ViewItem';
import ItemPreview from 'components/Selector/DSPSelector/SelectedDSPList/ItemPreview/ItemPreview';
import { TableHeader } from 'components/data-containers/BrowseEntities/shared/types';

import styled from 'styled-components/macro';

import { CheckboxLabel } from 'components/base/CheckboxDropdown/styles';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import { DefinitionLabel } from 'styles/base-classes';
import { List } from 'components/Selector/DSPSelector/SelectedDSPList/SelectedDSPList';
import {
  DragHandler,
  RemoveButton,
} from 'components/Selector/DSPSelector/SelectedDSPList/DSPItem/DSPItem';

const Content = styled(Box)`
  position: absolute;
  top: calc(100% + 10px);
  right: 56px;
  z-index: 1;
  background-color: ${({ theme }): string => theme.White};
  border: 1px solid #e9ebed;

  ${Box} ${CheckboxLabel} {
    text-transform: capitalize;
    text-align: left;
    & > span {
      color: ${({ theme }): string => theme.NightBlue};
      line-height: 2.4;
    }
  }

  ${Displayname} {
    color: ${({ theme }): string => theme.NightBlue};
    line-height: 2.4;
  }
`;

export const DraggedList = styled(List)`
  width: 100%;
  & > li {
    border-top: none;
    ${DragHandler}, ${RemoveButton} {
      padding: 0;
    }
    ${RemoveButton} {
      display: block !important;
      color: ${({ theme }): string => theme.CasperGrey};
    }
  }
`;

const BasicButton = styled(Button)`
  width: 100%;
  border: 1px solid ${({ theme }): string => theme.AccentGreen};
  background-color: ${({ theme }): string => theme.White};
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 3px;
`;

export const Label = styled(DefinitionLabel)`
  margin: 0.5rem 0 1.2rem;
  font-size: 0.6em;
  font-weight: bold;
`;

export type TTableHeader = TableHeader & { selected?: boolean };

export type TItemCustomViewExtended =
  | {
      selected?: boolean;
      label: string;
      value: string;
    }
  | TTableHeader;

interface ICustomizeView {
  onClick?: any;
  formatItems?: (item: TItemCustomViewExtended) => TItemCustomViewExtended;
  saveToApi: (headers: any[]) => void;
  savedItems?: TItemCustomViewExtended[];
  allAvailableItems?: any;
  displayBlockLabel?: string;
  orderBlockLabel?: string;
  canTurnOffAll?: boolean;
  minWidth?: string;
}

const CustomizeViewDropdown: React.FC<ICustomizeView> = (props) => {
  const {
    onClick,
    saveToApi,
    formatItems,
    savedItems,
    allAvailableItems,
    displayBlockLabel,
    orderBlockLabel,
    canTurnOffAll,
    minWidth = '490px',
  } = props;

  const isLargeScreen = useMedia('(min-width: 36rem)');

  const markSelectedItems = (
    header: TItemCustomViewExtended
  ): TItemCustomViewExtended => {
    const selectedHeader = (
      (savedItems && savedItems.length > 0) || canTurnOffAll
        ? savedItems
        : allAvailableItems
    ).find((h: TItemCustomViewExtended) => h.value === header.value);
    return selectedHeader ? { ...header, selected: true } : header;
  };

  const [tempItems, setTempItems] = useState(
    allAvailableItems
      .map((item: any) => (formatItems ? formatItems(item) : item))
      .map(markSelectedItems)
  );
  const [draggedItems, setDraggedItems] = useState(
    (savedItems && savedItems.length > 0) || canTurnOffAll
      ? savedItems
      : allAvailableItems
  );
  const isMinAllowedHeadersExceeded =
    tempItems.filter((header: TItemCustomViewExtended) => !!header.selected)
      .length <= 1;

  const handleChecked = ({
    value,
    selected,
  }: {
    value: string;
    selected: boolean;
  }): void => {
    if (!selected) {
      const item = tempItems.find(
        (header: TTableHeader) => header.value === value
      );
      if (item) {
        setDraggedItems([
          ...draggedItems,
          {
            ...item,
            selected: true,
          },
        ]);
      }
    } else {
      setDraggedItems(
        draggedItems.filter(
          (header: TItemCustomViewExtended) => header.value !== value
        )
      );
    }
    const selectedTableHeaders = tempItems.map(
      (header: TItemCustomViewExtended) =>
        header.value === value ? { ...header, selected: !selected } : header
    );
    setTempItems(selectedTableHeaders);
  };

  const excludeColumn = (value: string): void => {
    if (draggedItems.length > 1 || canTurnOffAll) {
      setDraggedItems(
        draggedItems.filter(
          (header: TItemCustomViewExtended) => header.value !== value
        )
      );
      setTempItems(
        tempItems.map((header: TItemCustomViewExtended) =>
          header.value === value ? { ...header, selected: false } : header
        )
      );
    }
  };

  const [DragDropBackend, backendOptions, isTouchDevice] =
    useReorderDragDropBackend();

  const changeOrder = useCallback(
    (start: number, end: number) => {
      const dragged = cloneDeep(draggedItems);
      dragged.splice(start, 1);
      dragged.splice(end, 0, draggedItems[start]);
      setDraggedItems(dragged);
    },
    [draggedItems]
  );
  const displayBlockTitle =
    displayBlockLabel || (isLargeScreen ? 'Display Columns' : 'Display');
  const orderBlockTitle =
    orderBlockLabel ||
    (isLargeScreen ? 'Order columns left to right' : 'Order info');

  return (
    <Content
      display="flex"
      minWidth={isLargeScreen ? minWidth : '223px'}
      flexDirection={isLargeScreen ? 'row' : 'column'}
    >
      <Box padding="1.4rem 1.8rem" borderRight="1px solid #e9ebed">
        <Label>{displayBlockTitle}</Label>
        {tempItems.map((item: TItemCustomViewExtended): JSX.Element => {
          return (
            <LabeledCheckbox
              key={`table-header-${item.label}`}
              disabled={
                item.selected && !canTurnOffAll && isMinAllowedHeadersExceeded
              }
              checkingParameters={{
                value: item.value,
                selected: !!item.selected,
              }}
              toggleCheckedHandler={handleChecked}
              label={item.label}
            />
          );
        })}
      </Box>
      <Box padding="1.4rem 1.8rem">
        <Label>{orderBlockTitle}</Label>
        {draggedItems.length > 0 && (
          <Box>
            <DndProvider backend={DragDropBackend} options={backendOptions}>
              <DraggedList>
                {draggedItems.map(
                  (
                    item: TItemCustomViewExtended,
                    index: number
                  ): JSX.Element => (
                    <ViewItem
                      index={index}
                      item={item}
                      key={`selected-item-${item.label}`}
                      changeOrder={changeOrder}
                      handleChecked={excludeColumn}
                      isRemovable={canTurnOffAll || draggedItems.length > 1}
                    />
                  )
                )}
                {isTouchDevice && <ItemPreview key="__preview" />}
              </DraggedList>
            </DndProvider>
          </Box>
        )}
        <BasicButton
          m={2}
          variant="secondary"
          onClick={(): void => {
            saveToApi(allAvailableItems);
            onClick();
          }}
          type="button"
        >
          Reset to Default
        </BasicButton>
        <Button
          m={2}
          variant="primary"
          onClick={(): void => {
            saveToApi(draggedItems);
            onClick();
          }}
          type="button"
          borderRadius="3px"
          width="100%"
          fontSize="11px"
        >
          Done
        </Button>
      </Box>
    </Content>
  );
};

export default CustomizeViewDropdown;
